import React from 'react';
import { Link } from 'react-router-dom';
import BotPageLayout from '../../components/BotPageLayout';
import {
  IconCode, IconBug, IconExplain, IconOptimize, IconFormat,
  IconConvert, IconReview, IconGenerate, IconDocs,
  IconLightning, IconGlobe, IconShield, IconTarget
} from '../../components/Icons';
import './CodeAssist.css';

const COMMANDS = [
  { Icon: IconBug,      cmd: '/debug',    params: 'code, language', desc: 'Identify and fix bugs. Returns a clear explanation and corrected code.', example: '/debug code:[snippet] language:python' },
  { Icon: IconExplain,  cmd: '/explain',  params: 'code, language', desc: 'Get a plain-English explanation of what any code block does.', example: '/explain code:[snippet] language:javascript' },
  { Icon: IconOptimize, cmd: '/optimize', params: 'code, language', desc: 'Receive concrete suggestions to improve performance, readability, and efficiency.', example: '/optimize code:[function] language:typescript' },
  { Icon: IconFormat,   cmd: '/format',   params: 'code, language', desc: 'Instantly format and lint code to match common style conventions.', example: '/format code:[messy code] language:java' },
  { Icon: IconConvert,  cmd: '/convert',  params: 'code, from, to', desc: 'Translate code from one language to another while preserving logic.', example: '/convert code:[snippet] from:python to:javascript' },
  { Icon: IconReview,   cmd: '/review',   params: 'code, language', desc: 'Get a code review with feedback on structure, security, and best practices.', example: '/review code:[file] language:rust' },
  { Icon: IconGenerate, cmd: '/generate', params: 'prompt, language', desc: 'Generate a code snippet from a plain-English description.', example: '/generate prompt:"sort a list of dicts by key" language:python' },
  { Icon: IconDocs,     cmd: '/docs',     params: 'code, language', desc: 'Auto-generate docstrings and inline comments for functions and classes.', example: '/docs code:[function] language:python' },
];

const HIGHLIGHTS = [
  { Icon: IconLightning, title: 'Instant responses',  desc: 'Average response under 2 seconds.' },
  { Icon: IconGlobe,     title: '20+ languages',      desc: 'From Python to Lua, fully supported.' },
  { Icon: IconShield,    title: 'Secure',              desc: 'Code inputs are not stored after processing.' },
  { Icon: IconTarget,    title: 'Accurate',            desc: 'Powered by advanced language models.' },
];

export default function CodeAssistFeatures() {
  return (
    <BotPageLayout
      botName="Code Assist"
      botSlug="code-assist"
      botIcon={<IconCode size={28} color="var(--blue-dark)" />}
      botTagline="Write, debug, and optimise code without leaving your Discord server."
    >
      <div className="container">
        <div className="animate-fade-up">
          <span className="eyebrow">Features</span>
          <h2 className="section-title">Everything you need<br />to code smarter.</h2>
          <div className="rule" />
        </div>

        <div className="ca-feature-highlights animate-fade-up delay-1">
          {HIGHLIGHTS.map(({ Icon, title, desc }) => (
            <div key={title} className="ca-fh-item">
              <div className="icon-wrap icon-wrap--sm"><Icon size={16} /></div>
              <div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 64 }}>
          <h3 className="ca-commands-title animate-fade-up">Slash Commands</h3>
          <p className="ca-commands-sub animate-fade-up delay-1">
            All commands use Discord's native slash command interface.
            Type <code>/</code> and select a command to get started.
          </p>
          <div className="ca-commands animate-fade-up delay-2">
            {COMMANDS.map(({ Icon, cmd, params, desc, example }) => (
              <div key={cmd} className="ca-command">
                <div className="ca-command__header">
                  <div className="icon-wrap icon-wrap--sm"><Icon size={15} /></div>
                  <span className="ca-command__name">{cmd}</span>
                  <span className="ca-command__params">{params}</span>
                </div>
                <p className="ca-command__desc">{desc}</p>
                <div className="ca-command__example">
                  <span className="ca-command__example-label">Example</span>
                  <code>{example}</code>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="ca-cta-strip animate-fade-up" style={{ marginTop: 56 }}>
          <p>Seen enough? Start using Code Assist today.</p>
          <Link to="/code-assist/invite" className="btn btn--dark">Invite to Server</Link>
        </div>
      </div>
    </BotPageLayout>
  );
}